import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './EventAnalysisDetailPage.css';

interface Event {
  id: string;
  timestamp: string;
  type: string;
  namespace: string;
  pod: string;
  source: string; // 누가 트리거했는가 (프로세스/사용자)
  action: 'Allowed' | 'Blocked';
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  details: string;
}

const EventAnalysisDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const eventData: Event[] = useMemo(() => ([
    { id: '1', timestamp: '2024-01-15 14:35:22', type: 'API Call', namespace: 'default', pod: 'web-app-1', source: 'curl (uid: 1000)', action: 'Allowed', severity: 'Low', details: 'GET /api/users - Response 200' },
    { id: '2', timestamp: '2024-01-15 14:34:55', type: 'File Access', namespace: 'kube-system', pod: 'etcd-0', source: 'etcd (uid: 65534)', action: 'Allowed', severity: 'Medium', details: '/var/lib/etcd/member/snap/db - Read access' },
    { id: '3', timestamp: '2024-01-15 14:34:12', type: 'Process Exec', namespace: 'production', pod: 'api-gateway-1', source: '/bin/bash (uid: 0)', action: 'Blocked', severity: 'High', details: 'Process: /bin/bash - Unauthorized execution' },
    { id: '4', timestamp: '2024-01-15 14:33:45', type: 'Network Traffic', namespace: 'default', pod: 'db-1', source: 'mysqld (uid: 999)', action: 'Allowed', severity: 'Low', details: 'TCP 3306 - MySQL traffic' },
    { id: '5', timestamp: '2024-01-15 14:33:10', type: 'Capability Use', namespace: 'production', pod: 'worker-1', source: 'java (uid: 999)', action: 'Blocked', severity: 'Critical', details: 'CAP_SYS_ADMIN - Privileged capability usage' },
    { id: '6', timestamp: '2024-01-15 14:32:38', type: 'API Call', namespace: 'default', pod: 'web-app-2', source: 'node (uid: 1000)', action: 'Allowed', severity: 'Low', details: 'POST /api/data - Response 201' },
    { id: '7', timestamp: '2024-01-15 14:32:05', type: 'File Access', namespace: 'default', pod: 'cache-1', source: 'redis-server (uid: 1001)', action: 'Allowed', severity: 'Low', details: '/etc/redis/redis.conf - Read access' },
    { id: '8', timestamp: '2024-01-15 14:31:30', type: 'Network Traffic', namespace: 'production', pod: 'api-1', source: 'python (uid: 1000)', action: 'Blocked', severity: 'High', details: 'Outbound connection - Unknown destination' },
  ]), []);

  const filteredEvents = useMemo(() => {
    return eventData.filter(event => {
      const typeMatch = filterType === 'all' || event.type === filterType;
      const severityMatch = filterSeverity === 'all' || event.severity === filterSeverity;
      return typeMatch && severityMatch;
    });
  }, [filterType, filterSeverity, eventData]);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical': return '#ff4444';
      case 'High': return '#ff8c00';
      case 'Medium': return '#ffd700';
      case 'Low': return '#22c55e';
      default: return 'var(--text-secondary)';
    }
  };

  const getActionColor = (action: string) => {
    return action === 'Blocked' ? '#ff4444' : '#22c55e';
  };

  return (
    <div className="detail-page event-analysis">
      <div className="detail-header">
        <h1>{t('eventAnalysis.detailTitle')}</h1>
        <p>{t('eventAnalysis.detailDescription')}</p>
      </div>

      {/* 필터 섹션 */}
      <section className="filter-section">
        <div className="filter-group">
          <label>{t('eventAnalysis.filters.eventType')}</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">{t('eventAnalysis.filters.all')}</option>
            <option value="API Call">API Call</option>
            <option value="File Access">File Access</option>
            <option value="Network Traffic">Network Traffic</option>
            <option value="Process Exec">Process Exec</option>
            <option value="Capability Use">Capability Use</option>
          </select>
        </div>
        <div className="filter-group">
          <label>{t('eventAnalysis.filters.severityLevel')}</label>
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
            <option value="all">{t('eventAnalysis.filters.all')}</option>
            <option value="Critical">Critical</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
        <div className="filter-stat">
          <span>{t('eventAnalysis.filters.searchResults')}: <strong>{filteredEvents.length}</strong></span>
        </div>
      </section>

      {/* 테이블 및 상세 정보 */}
      <div className="content-layout">
        {/* 데이터 테이블 */}
        <section className="table-section">
          <div className="table-header">
            <div>{t('eventAnalysis.columns.timestamp')}</div>
            <div>{t('eventAnalysis.columns.type')}</div>
            <div>{t('eventAnalysis.columns.source')}</div>
            <div>{t('eventAnalysis.columns.pod')}</div>
            <div>{t('eventAnalysis.columns.action')}</div>
            <div>{t('eventAnalysis.columns.severity')}</div>
          </div>
          <div className="table-body">
            {filteredEvents.map((event) => (
              <div
                key={event.id}
                className={`table-row ${selectedEvent?.id === event.id ? 'active' : ''}`}
                onClick={() => setSelectedEvent(event)}
              >
                <div>{event.timestamp}</div>
                <div>{event.type}</div>
                <div>{event.source}</div>
                <div>{event.pod}</div>
                <div>
                  <span className="action-badge" style={{backgroundColor: `${getActionColor(event.action)}20`, color: getActionColor(event.action)}}>
                    {event.action}
                  </span>
                </div>
                <div>
                  <span className="severity-badge" style={{backgroundColor: `${getSeverityColor(event.severity)}20`, color: getSeverityColor(event.severity)}}>
                    {event.severity}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 상세 정보 */}
        <section className="detail-section">
          {selectedEvent ? (
            <div className="detail-card">
              <div className="detail-title">이벤트 상세</div>
              <div className="detail-content">
                <div className="detail-row">
                  <div className="detail-label">이벤트 ID</div>
                  <div className="detail-value">{selectedEvent.id}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">시간</div>
                  <div className="detail-value">{selectedEvent.timestamp}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">타입</div>
                  <div className="detail-value">{selectedEvent.type}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">네임스페이스</div>
                  <div className="detail-value">{selectedEvent.namespace}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Pod</div>
                  <div className="detail-value">{selectedEvent.pod}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">액션</div>
                  <div className="detail-value">
                    <span className="action-badge" style={{backgroundColor: `${getActionColor(selectedEvent.action)}20`, color: getActionColor(selectedEvent.action)}}>
                      {selectedEvent.action}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">심각도</div>
                  <div className="detail-value">
                    <span className="severity-badge" style={{backgroundColor: `${getSeverityColor(selectedEvent.severity)}20`, color: getSeverityColor(selectedEvent.severity)}}>
                      {selectedEvent.severity}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">상세 내용</div>
                  <div className="detail-value detail-description">{selectedEvent.details}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-card empty-detail">
              <div className="empty-state-message">
                <p>📋 목록에서 이벤트를 선택하세요.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default EventAnalysisDetailPage;
